import { useState, useEffect } from "react";

const PremiumLoader = ({
  isLoading = true,
  text = "Finding Best Deals",
  subText = "Searching parking options for you...",
  duration = 3000,
  onComplete = null,
}) => {
  const [progress, setProgress] = useState(0);
  const [step, setStep]         = useState(0);

  const steps = [
    "Connecting to providers...",
    "Comparing prices...",
    "Checking availability...",
    "Almost ready...",
  ];

  /* ── progress bar ── */
  useEffect(() => {
    if (!isLoading) { setProgress(0); return; }
    const inc = 100 / (duration / 40);
    const id  = setInterval(() => {
      setProgress((p) => {
        if (p + inc >= 100) {
          clearInterval(id);
          if (onComplete) setTimeout(onComplete, 400);
          return 100;
        }
        return p + inc;
      });
    }, 40);
    return () => clearInterval(id);
  }, [isLoading, duration, onComplete]);

  /* ── step text ── */
  useEffect(() => {
    if (!isLoading) return;
    const id = setInterval(() => setStep((s) => (s + 1) % steps.length), duration / steps.length);
    return () => clearInterval(id);
  }, [isLoading, duration]);

  if (!isLoading) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-primary overflow-hidden">

      {/* ── animated bg blobs ── */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-lightprimary/10 blur-[120px] animate-pulse" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-secondary/10 blur-[100px] animate-pulse" style={{ animationDelay: "1s" }} />

      {/* ── card ── */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-sm mx-4">

        {/* ── plane animation ── */}
        <div className="relative w-full h-20 mb-6 overflow-hidden">
          {/* runway */}
          <div className="absolute bottom-3 left-0 right-0 h-px bg-white/10" />
          {/* dashes */}
          <div className="absolute bottom-3 left-0 right-0 flex gap-4 overflow-hidden">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-px w-8 bg-secondary/40 flex-shrink-0"
                style={{ animation: `slideLeft 1.5s linear infinite`, animationDelay: `${i * 0.2}s` }} />
            ))}
          </div>
          {/* plane */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2"
            style={{ animation: "floatPlane 2s ease-in-out infinite" }}>
            <svg width="56" height="28" viewBox="0 0 56 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* fuselage */}
              <ellipse cx="28" cy="14" rx="22" ry="6" fill="#EEFE3E" />
              {/* nose */}
              <path d="M50 14 Q58 14 54 11 L50 11 Z" fill="#EEFE3E" />
              {/* tail fin */}
              <path d="M8 14 L4 6 L12 10 Z" fill="#0c9e82" />
              {/* main wing */}
              <path d="M24 14 L18 4 L36 10 L36 14 Z" fill="#00261E" stroke="#EEFE3E" strokeWidth="0.5" />
              {/* rear wing */}
              <path d="M12 14 L8 8 L18 12 L18 14 Z" fill="#00261E" stroke="#EEFE3E" strokeWidth="0.5" />
              {/* windows */}
              <circle cx="38" cy="13" r="1.5" fill="#00261E" />
              <circle cx="33" cy="13" r="1.5" fill="#00261E" />
              <circle cx="28" cy="13" r="1.5" fill="#00261E" />
            </svg>
          </div>
          {/* exhaust dots */}
          <div className="absolute bottom-5 left-[calc(50%-40px)] flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-secondary/60"
                style={{ animation: `fadeOut 1s ease-out infinite`, animationDelay: `${i * 0.25}s` }} />
            ))}
          </div>
        </div>

        {/* ── logo / brand ── */}
        <div className="flex items-center gap-2 mb-6">
          <img src="/logo.webp" alt="Airport Parking Deals" className="h-8 w-auto brightness-0 invert" />
        </div>

        {/* ── main text ── */}
        <h2 className="text-white text-xl font-black uppercase tracking-widest text-center mb-1">
          {text}
        </h2>
        <p className="text-white/50 text-xs font-semibold text-center mb-8 tracking-wide">
          {subText}
        </p>

        {/* ── progress bar ── */}
        <div className="w-full mb-3">
          <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full relative overflow-hidden transition-all duration-100"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, #0c9e82, #EEFE3E)",
              }}
            >
              {/* shimmer */}
              <div className="absolute inset-0"
                style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)", animation: "shimmer 1.2s linear infinite" }} />
            </div>
          </div>
        </div>

        {/* ── progress % + step ── */}
        <div className="flex items-center justify-between w-full mb-6">
          <span className="text-secondary text-xs font-black tabular-nums">{Math.floor(progress)}%</span>
          <span className="text-white/40 text-xs font-semibold">{steps[step]}</span>
        </div>

        {/* ── 3 dot indicators ── */}
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-secondary"
              style={{ animation: `bounce 1.2s ease-in-out infinite`, animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>

      </div>

      {/* ── keyframes ── */}
      <style>{`
        @keyframes floatPlane {
          0%, 100% { transform: translateX(-50%) translateY(0px); }
          50%       { transform: translateX(-50%) translateY(-6px); }
        }
        @keyframes slideLeft {
          0%   { transform: translateX(0); opacity: 1; }
          100% { transform: translateX(-200px); opacity: 0; }
        }
        @keyframes fadeOut {
          0%   { opacity: 0.8; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.3) translateX(-12px); }
        }
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40%            { transform: scale(1.2); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default PremiumLoader;
